import s from './Mine.module.sass';

import { useDispatch, useSelector } from 'react-redux';
import React, { useEffect, useState } from 'react';
import { mineActions, selectMine } from '$store/mine';
import { Loader, MineButton, MineOnboarding } from '$shared/components';
import { Button, Header, LottieIcon, Placeholder } from '$uikit';
import { useCurrentUserInfo, useTranslator, useUserInfo } from '$hooks';
import {
  IcCards28,
  IcChevron16,
  IcHot28,
  IcLogo40,
  IcPlusCircle28,
  IcUsers28,
} from '$assets';
import { checkShakeAccess } from '../../services/shake';
import { goTo, showModal } from '$navigation/helper';
import {
  MINE_BOOST,
  MINE_EXPLORE,
  MINE_FRIENDS,
  MINE_SQUAD,
  MINE_TOP,
} from '$shared/constants/pages';
import { Energy } from '$core/Mine/Energy/Energy';
import classNames from 'classnames';
import { isAndroid, isIOS } from '$utils';

import { ReactComponent as TabTop } from './assets/tab_top.svg';
import { ReactComponent as TabBoost } from './assets/tab_boost.svg';
import { prettifyNumber } from '$utils/number';

export const Mine = () => {
  const dispatch = useDispatch();
  const t = useTranslator();
  const curUser = useCurrentUserInfo();
  const { loadingStatus, coins, squad, cantEarn, onboardingPassed } =
    useSelector(selectMine);
  const squadOwner = useUserInfo(squad?.userId ?? 0);
  const [hasPermission, setHasPermission] = useState(checkShakeAccess());
  const isModel = curUser?.verified === 2;

  useEffect(() => {
    if (!hasPermission && checkShakeAccess()) {
      setHasPermission(true);
    }
  }, [loadingStatus]);

  const requestPermission = () => {
    if (window.DeviceMotionEvent) {
      if ('requestPermission' in window.DeviceMotionEvent) {
        // @ts-ignore
        DeviceMotionEvent.requestPermission().then((response) => {
          if (response === 'granted') {
            setHasPermission(true);
          }
        });
      } else {
        setHasPermission(true);
      }
    }
  };

  const handleLoad = () => {
    dispatch(mineActions.load());
  };

  useEffect(() => {
    handleLoad();
  }, []);

  function renderContent() {
    if (cantEarn) {
      return (
        <Placeholder
          iconSize={120}
          title="Вы не можете зарабатывать :("
          icon={<LottieIcon name="facecontrol" loop />}
          centered
        >
          Для запуска игры нужно иметь активную подписку на любую модель.
        </Placeholder>
      );
    }

    if (loadingStatus === 'failed') {
      return (
        <Placeholder
          title={t('failed_title')}
          centered
          actions={<Button onClick={handleLoad}>{t('failed_button')}</Button>}
        >
          {t('failed_caption')}
        </Placeholder>
      );
    }

    if (loadingStatus === 'loading' || !squad) {
      return <Loader isWrapped />;
    }

    // if (!isIOS() && !isAndroid()) {
    //   return (
    //     <Placeholder
    //       iconSize={200}
    //       icon={
    //         <img
    //           src={require('./qr.png').default}
    //           style={{ width: 200, height: 200, borderRadius: 16 }}
    //         />
    //       }
    //       title="Go back via mobile!"
    //       centered
    //     >
    //       A gyroscope is required to start the game.
    //     </Placeholder>
    //   );
    // }

    // if (!hasPermission) {
    //   return (
    //     <Placeholder
    //       title="Allow access to the motion sensor"
    //       icon={<IcLogo40 />}
    //       iconSize={120}
    //       centered
    //       actions={<Button onClick={requestPermission}>Allow access</Button>}
    //     >
    //       To start mining coins, shake your phone. To do this, you need to give access to
    //       the motion sensor.
    //     </Placeholder>
    //   );
    // }

    return (
      <div className={s.wrap}>
        <div className={s.bg} style={{ backgroundImage: `url(${squad.url})` }} />
        <div>
          <div className={s.balanceWrap}>
            <div className={s.balanceIcon}>
              <IcLogo40 />
            </div>
            <div className={s.balance} key={coins}>
              {prettifyNumber(coins)}
            </div>
          </div>
          {!isModel && (
            <div className={s.boostWrap}>
              <div className={s.boostBtn} onClick={() => goTo(MINE_BOOST)}>
                <IcHot28 />
                <div className={s.boostLabel}>Boost</div>
                <div className={s.boostChevron}>
                  <IcChevron16 />
                </div>
              </div>
            </div>
          )}
        </div>
        <MineButton />
        <Energy />
        <div className={s.footer}>
          <div
            className={classNames({
              [s.footerItem]: true,
              [s.footerItemSquad]: true,
            })}
            onClick={() => goTo(MINE_SQUAD, { id: squad.id })}
          >
            <div className={s.footerItemIcon}>
              <div
                className={s.footerItemIconAva}
                style={{ backgroundImage: `url(${squadOwner.photo})` }}
              />
            </div>
            <div className={s.footerItemLabel}>My Squad</div>
          </div>
          <div
            className={classNames({
              [s.footerItem]: true,
              [s.footerItemTop]: true,
            })}
            onClick={() => goTo(MINE_TOP)}
          >
            <div className={s.footerItemIcon}>
              <TabTop />
            </div>
            <div className={s.footerItemLabel}>Top</div>
          </div>
          {isModel && (
            <div
              className={classNames({
                [s.footerItem]: true,
                [s.footerItemBoost]: true,
              })}
              onClick={() => goTo(MINE_BOOST)}
            >
              <div className={s.footerItemIcon}>
                <TabBoost />
              </div>
              <div className={s.footerItemLabel}>Boost</div>
            </div>
          )}
          {!isModel && (
            <div
              className={classNames({
                [s.footerItem]: true,
                [s.footerItemBoost]: true,
              })}
              onClick={() => goTo(MINE_FRIENDS)}
            >
              <div className={s.footerItemIcon}>
                <IcUsers28 />
              </div>
              <div className={s.footerItemLabel}>Friends</div>
            </div>
          )}
          <div
            className={classNames({
              [s.footerItem]: true,
              [s.footerItemCards]: true,
            })}
            onClick={() => goTo(MINE_EXPLORE)}
          >
            <div className={s.footerItemIcon}>
              <IcCards28 />
            </div>
            <div className={s.footerItemLabel}>Cards</div>
          </div>
        </div>
      </div>
    );
  }

  // if (!onboardingPassed && loadingStatus === '') {
  //   return (
  //     <>
  //       <Header showBackButton />
  //       <MineOnboarding />
  //     </>
  //   );
  // }

  return (
    <>
      <Header
        showBackButton
        rightIcons={[
          curUser?.verified === 2
            ? {
                icon: <IcPlusCircle28 />,
                onClick: () => showModal('mine_squad_new'),
              }
            : null,
        ]}
      >
        PLACE Coin Mining (Beta)
      </Header>
      {renderContent()}
    </>
  );
};
